import React, { useState, useMemo } from "react";
import { motion } from "framer-motion";
import {
  Search,
  Eye,
  Download,
  FileText,
  X,
  Trash2,
} from "lucide-react";
import api, { adminReq } from "../../lib/api";
import { toast } from "sonner";
import useSelection from "../../hooks/useSelection";
import SelectionBar from "../../components/SelectionBar";
import CheckboxCell from "../../components/CheckboxCell";
import { ConfirmModal } from "./Trash";

const statusBadge = (s) => {
  const map = {
    approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    rejected: "bg-red-50 text-red-700 border-red-200",
    submitted: "bg-sky-50 text-sky-700 border-sky-200",
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border capitalize ${
        map[s] || map.submitted
      }`}
    >
      {s || "submitted"}
    </span>
  );
};

export default function AdminApplications({ rows, onDownload, onRefresh }) {
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const filtered = useMemo(() => {
    return (rows || []).filter((r) => {
      const matchQ =
        !q ||
        [
          r.full_name,
          r.email,
          r.program,
          r.nationality,
          r.referral_recruiter_name,
        ]
          .filter(Boolean)
          .some((v) => v.toLowerCase().includes(q.toLowerCase()));
      const matchS = statusFilter === "all" || r.status === statusFilter;
      return matchQ && matchS;
    });
  }, [rows, q, statusFilter]);

  const sel = useSelection(filtered);

  const bulkDelete = async (ids) => {
    try {
      await api.post(
        "/admin/applications/bulk-delete",
        { ids },
        adminReq()
      );
      toast.success(`Moved ${ids.length} to trash`);
      sel.clear();
      if (onRefresh) onRefresh();
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-4 flex flex-col sm:flex-row gap-3">
        <div className="flex-1 flex items-center gap-2 bg-slate-100 rounded-lg px-3 py-2">
          <Search className="w-4 h-4 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, email, program, nationality..."
            className="bg-transparent outline-none text-sm flex-1"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white"
        >
          <option value="all">All statuses</option>
          <option value="submitted">Submitted</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50/80 text-left text-slate-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3 w-10">
                  <CheckboxCell
                    checked={sel.allSelected}
                    indeterminate={sel.someSelected}
                    onChange={sel.toggleAll}
                    ariaLabel="Select all"
                  />
                </th>
                <th className="px-4 py-3">Applicant</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Program</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Source</th>
                <th className="px-4 py-3">Submitted</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td
                    colSpan={8}
                    className="text-center text-slate-400 text-sm py-12"
                  >
                    No applications found
                  </td>
                </tr>
              ) : (
                filtered.map((r, idx) => {
                  const checked = sel.isSelected(r.id);
                  return (
                    <motion.tr
                      key={r.id}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.02, duration: 0.25 }}
                      onClick={() => sel.toggle(r.id)}
                      className={`border-t border-slate-100 cursor-pointer ${
                        checked
                          ? "bg-amber-50/60"
                          : "hover:bg-slate-50/70"
                      }`}
                    >
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <CheckboxCell
                          checked={checked}
                          onChange={() => sel.toggle(r.id)}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#1E3A8A] to-[#3B5BC9] text-white flex items-center justify-center font-semibold text-xs">
                            {r.full_name?.slice(0, 1).toUpperCase()}
                          </div>
                          <div>
                            <div className="font-medium text-slate-800">
                              {r.full_name}
                            </div>
                            <div className="text-xs text-slate-400">
                              {r.nationality}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-600">{r.email}</td>
                      <td className="px-4 py-3 text-slate-600">
                        {r.program || "—"}
                      </td>
                      <td className="px-4 py-3">{statusBadge(r.status)}</td>
                      <td className="px-4 py-3 text-slate-600 text-xs">
                        {r.referral_recruiter_name || (
                          <span className="text-slate-400">Direct</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-500 text-xs">
                        {new Date(r.created_at).toLocaleDateString()}
                      </td>
                      <td
                        className="px-4 py-3"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setSelected(r)}
                            className="inline-flex items-center gap-1 text-cct-blue text-xs font-semibold hover:underline"
                          >
                            <Eye className="w-3.5 h-3.5" /> View
                          </button>
                          <button
                            onClick={() =>
                              setConfirm({ ids: [r.id] })
                            }
                            className="inline-flex items-center gap-1 text-red-500 text-xs font-semibold hover:underline"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <SelectionBar
        count={sel.count}
        onClear={sel.clear}
        noun="application"
        actions={[
          {
            label: "Move to Trash",
            icon: Trash2,
            variant: "danger",
            onClick: () => setConfirm({ ids: sel.selectedIds }),
          },
        ]}
      />

      {confirm && (
        <ConfirmModal
          title="Move to Trash?"
          message={`${confirm.ids.length} application${
            confirm.ids.length !== 1 ? "s" : ""
          } will be moved to Trash. You can restore them later.`}
          confirmLabel="Move to Trash"
          danger
          onCancel={() => setConfirm(null)}
          onConfirm={() => {
            bulkDelete(confirm.ids);
            setConfirm(null);
          }}
        />
      )}

      {selected && (
        <DetailDrawer
          row={selected}
          onClose={() => setSelected(null)}
          onDownload={onDownload}
        />
      )}
    </div>
  );
}

function DetailDrawer({ row, onClose, onDownload }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex justify-end"
      onClick={onClose}
    >
      <motion.div
        initial={{ x: 400, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: 400, opacity: 0 }}
        transition={{ type: "spring", damping: 22 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg bg-white h-full overflow-y-auto shadow-2xl"
      >
        <div className="p-6 border-b border-slate-200 flex justify-between items-start sticky top-0 bg-white z-10">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-gold font-semibold">
              Application
            </div>
            <h3 className="font-display text-2xl font-semibold text-cct-blue mt-1">
              {row.full_name}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-3 text-sm">
          {Object.entries(row)
            .filter(
              ([k]) =>
                ![
                  "_id",
                  "documents",
                  "password_hash",
                  "id",
                  "deleted_at",
                ].includes(k)
            )
            .map(([k, v]) => (
              <div
                key={k}
                className="grid grid-cols-3 gap-3 py-2 border-b border-slate-100"
              >
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                  {k.replace(/_/g, " ")}
                </div>
                <div className="col-span-2 text-slate-800 break-words">
                  {typeof v === "boolean"
                    ? v
                      ? "Yes"
                      : "No"
                    : String(v ?? "—")}
                </div>
              </div>
            ))}
          {row.documents?.length > 0 && (
            <div className="pt-3">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
                Documents
              </div>
              <div className="space-y-2">
                {row.documents.map((d) => (
                  <div
                    key={d.file_id}
                    className="flex items-center justify-between border border-slate-200 rounded-md px-3 py-2.5 hover:bg-slate-50"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText className="w-4 h-4 text-cct-blue shrink-0" />
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-800 truncate">
                          {d.label?.replace(/_/g, " ") || d.filename}
                        </div>
                        <div className="text-xs text-slate-500 truncate">
                          {d.filename}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => onDownload(d.file_id, d.filename)}
                      className="inline-flex items-center gap-1 text-cct-blue hover:underline text-xs font-semibold shrink-0"
                    >
                      <Download className="w-3.5 h-3.5" /> Download
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
